import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Virtuoso } from "react-virtuoso";
import { useSnackbar } from "notistack";
import {
  ERROR_SNACKBAR,
  SETTLEMENT_STATUS_TO_COLOR,
  SETTLEMENTS_ENDPOINT,
  SUCCESS_SNACKBAR,
} from "../../../../consts";
import { useInvoices } from "../../../../api/invoices";
import { useHasPermission } from "../../../../contexts/auth";
import { formatCounterValue, formatDateTime, formatNumber } from "../../../../utils";
import axios from "../../../../api/axios";
import styles from "./saleSummaryDetail.module.css";
import CustomCircularProgress from "../../../../components/ui/customCircularProgress/customCircularProgress.component";
import {
  AddLink,
  Construction,
  EditNote,
  Email,
  EventBusy,
  Handshake,
  LinkOff,
  Person4,
  PictureAsPdf,
  ReceiptLong,
  Speed,
  SupportAgent,
} from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import { alpha, Theme } from "@mui/material/styles";
import CustomModal from "../../../../components/ui/modal/modal.component";
import { SettlementActionModal } from "./settlementActionModal.component";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { SendReminder } from "../../../../components/settlements/sendReminder/sendReminder.component";

const SETTLEMENT_STATUS_CONFIRMED = "C";
const SETTLEMENT_STATUS_NEEDS_INVESTIGATION = "N";
const CAN_CHANGE_CONFIRMED_SETTLEMENT_PERMISSION =
  "settlement.can_change_confirmed_settlement";

// The list is virtualized: only the visible rows (plus a small buffer) are
// mounted, and pages are fetched on demand for whatever range is in view - so
// the user can drag the scrollbar straight to the middle of a large month and
// only the page(s) under the viewport are requested, instead of every page in
// between. One "row" is one paginated group: a single contract when ungrouped,
// or a whole client (with its contracts) when grouped by client.
const SALE_SUMMARY_PAGE_SIZE = 10;
// Fallback row height, only used before the manifest (which carries a per-row
// size hint) is available.
const SALE_SUMMARY_ESTIMATED_ROW_HEIGHT = 360;

// One extra line per position table once the counter readings line is
// switched on - only feeds the placeholder estimate below, real cards are
// measured.
const COUNTER_READINGS_LINE_HEIGHT = 30;

// A rough rendered height for one not-yet-loaded row, from the manifest's
// settlement/contract counts. It only needs to be close enough that the
// scrollbar barely shifts when the real card replaces the placeholder.
const estimateRowHeight = (
  row?: SaleSummaryManifestRow,
  perSettlementExtra: number = 0
): number => {
  if (!row) {
    return SALE_SUMMARY_ESTIMATED_ROW_HEIGHT;
  }
  const raw =
    row.type === "client"
      ? 72 + row.contract_count * 84 + row.settlement_count * (168 + perSettlementExtra)
      : 104 +
        row.settlement_count * (176 + perSettlementExtra) +
        (row.settlement_count > 1 ? 120 : 0);
  return Math.min(4000, Math.max(140, Math.round(raw)));
};

// Small colored circular badge behind the "newly attached"/"term ended"
// equipment icons - plain tinted icons alone read as too faint at a glance.
const statusIconBadgeSx = (color: "success" | "warning") => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  borderRadius: "50%",
  color: `${color}.main`,
  bgcolor: (theme: Theme) => alpha(theme.palette[color].main, 0.15),
});

type SettlementPositionSummary = {
  position_id: number;
  counter_kind: string;
  // Raw counter kind code (BP/CP/SP) next to its label, for the short badges
  // in the counter strip - see COUNTER_KIND_SHORT_LABEL_KEYS.
  counter_kind_code?: string | null;
  last_counter_value: number | null;
  last_read_at?: string | null;
  // Null while the position is still waiting for its reading.
  new_counter_value: number | null;
  counter_difference: number;
  price_per_unit: number;
  included_in_rent: number;
  new_read_at?: string | null;
  // Where the billed reading came from: a CounterSource code (see backend
  // consts.py) plus the backend's own label, used as a fallback for a code
  // this frontend does not know yet.
  new_counter_source?: string | null;
  new_counter_source_display?: string | null;
  quantity_over_included_in_rent: number | null;
};

// One icon per counter source, so where a reading came from reads at a glance
// while the wording stays in the tooltip - the strip may only cost one line.
const COUNTER_SOURCE_ICONS: Record<string, typeof Email> = {
  U: SupportAgent,
  C: Person4,
  P: PictureAsPdf,
  E: Email,
};

const COUNTER_SOURCE_COLORS: Record<string, string> = {
  U: "success.main",
  C: "primary.main",
  P: "warning.main",
  E: "info.main",
};

// Source labels are translated here rather than taken from the backend's
// *_display, so they follow the interface language like every other label in
// this view; the backend label is the fallback for an unknown code.
const COUNTER_SOURCE_LABEL_KEYS: Record<string, string> = {
  U: "User",
  C: "Client",
  P: "PDF",
  E: "Email report",
};

// Short badges for the counter strip ("Mono 12 345"); the full kind label is
// in the tooltip. Looked up through t() - keep these keys in i18n.js.
const COUNTER_KIND_SHORT_LABEL_KEYS: Record<string, string> = {
  BP: "Black",
  CP: "Color",
  SP: "Scan",
};

type CounterReading = {
  key: string;
  kindCode: string | null;
  kindLabel: string;
};

type CounterReadingGroup = {
  key: string;
  source: string | null;
  sourceDisplay: string | null;
  readAt: string | null;
  readings: CounterReading[];
};

// Counter readings arrive as one batch per machine: every kind read at the
// same moment, from the same source, so black and color pages share a source
// and a read date and differ only in value. Grouping on (source, read date)
// therefore collapses the usual case into a single line above the position
// table - and on the rare occasion the kinds did not arrive together, each
// batch keeps its own entry, named by the kinds it covers, instead of one of
// the dates being silently dropped.
const buildCounterReadingGroups = (
  positions: SettlementPositionSummary[]
): CounterReadingGroup[] => {
  const groups = new Map<string, CounterReadingGroup>();

  positions.forEach((position) => {
    // No source and no read date means the position is still waiting for its
    // reading - there is nothing to attribute yet.
    if (!position.new_counter_source && !position.new_read_at) {
      return;
    }

    const groupKey = `${position.new_counter_source ?? ""}|${position.new_read_at ?? ""}`;
    const group = groups.get(groupKey) ?? {
      key: groupKey,
      source: position.new_counter_source ?? null,
      sourceDisplay: position.new_counter_source_display ?? null,
      readAt: position.new_read_at ?? null,
      readings: [],
    };

    // A common-counter term bills one physical reading through several
    // positions (one per machine sharing the counter), so the same kind can
    // land here more than once - name it once.
    const readingKey = position.counter_kind_code ?? position.counter_kind;
    if (!group.readings.some((reading) => reading.key === readingKey)) {
      group.readings.push({
        key: readingKey,
        kindCode: position.counter_kind_code ?? null,
        kindLabel: position.counter_kind,
      });
    }

    groups.set(groupKey, group);
  });

  return Array.from(groups.values());
};

type SettlementSummaryItem = {
  settlement_id: number;
  status: string;
  status_display: string;
  sale_date?: string | null;
  settlement_kind?: string | null;
  settlement_kind_display?: string | null;
  is_common_counter?: boolean;
  total_calculated: number;
  calculated_sum: number;
  extra_positions_total: number;
  inflation_rate_percent?: number | string | null;
  total_overridden: number | null;
  display_total: number;
  equipment_name: string;
  equipment_serial: string;
  equipments?: { id: number; name: string; serial: string | null }[];
  rent_price: number;
  proportions_factor?: number | string | null;
  // Whether the contract term behind this settlement has already been ended
  // (deactivated), and whether this is that term's first-ever settlement
  // (newly added to the contract) - see get_sale_summary (backend selectors.py).
  term_ended?: boolean;
  is_first_settlement?: boolean;
  profitability_net?: number | string | null;
  profitability_cost?: number | string | null;
  profitability_percent?: number | string | null;
  profitability_job_count?: number | null;
  additional_costs?: number | string | null;
  // The live invoice raised for this settlement, if any - see
  // _invoice_by_settlement_id in the backend selectors.
  invoice?: {
    id: number;
    number: string | null;
    status: string;
    is_paid: boolean;
  } | null;
  positions: SettlementPositionSummary[];
};

type SettlementTotalsSummary = {
  settlement_count: number;
  usage_calculated: number;
  extra_positions_total: number;
  total_calculated: number;
  total_overridden: number;
  display_total: number;
  pending_count: number;
  usage_by_kind: Record<string, number>;
  profitability_net?: number | string | null;
  profitability_cost?: number | string | null;
  profitability_job_count?: number | null;
  additional_costs?: number | string | null;
  has_profitability?: boolean;
};

type SaleDateContractSummary = {
  contract_id: number;
  contract_number: string;
  contract_internal_name?: string | null;
  is_brutto?: boolean;
  // Whether the contract itself (not just one of its terms) has already
  // ended - see get_sale_summary (backend selectors.py, Contract.ended).
  contract_ended?: boolean;
  settlement_kind: string;
  settlements: SettlementSummaryItem[];
  totals: SettlementTotalsSummary;
};

type ClientOnSaleSummary = {
  id: number;
  name: string;
  short_name: string | null;
  nip: string | null;
  regon: string | null;
  krs: string | null;
  pesel: string | null;
};

type SaleSummaryResponse = {
  client: ClientOnSaleSummary;
  contracts: SaleDateContractSummary[];
};

// One lightweight entry per group for the whole filter set, returned on page 1
// of the sale-summary response - see serialize_sale_summary_manifest (backend
// selectors.py). Lets the list render a complete, correctly-sized skeleton
// before any detail page loads.
type SaleSummaryManifestRow =
  | {
      type: "contract";
      id: number;
      contract_id: number;
      contract_number: string;
      contract_internal_name: string | null;
      client_id: number;
      client_name: string;
      client_short_name: string | null;
      settlement_count: number;
    }
  | {
      type: "client";
      id: number;
      client_id: number;
      client_name: string;
      client_short_name: string | null;
      client_nip: string | null;
      contract_count: number;
      settlement_count: number;
    };

type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  manifest?: SaleSummaryManifestRow[];
};

type SaleSummaryDetailProps = {
  saleDate?: string;
  contractId?: string;
  search?: string;
  showHeader?: boolean;
};

const formatInflationRatePercent = (value: unknown): string => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return `${formatNumber(value)}`;
};

const hasInflationRate = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") {
    return false;
  }
  return Number(value) !== 0;
};

export const SaleSummaryDetail = ({
  saleDate: saleDateProp,
  contractId: contractIdProp,
  search = "",
  showHeader = true,
}: SaleSummaryDetailProps) => {
  const { id, saleDate: saleDateFromParams } = useParams();
  const saleDate = saleDateProp ?? saleDateFromParams;
  const contractId = contractIdProp ?? id;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewContracts = useHasPermission("settlement.view_contract");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const canViewProfitability = useHasPermission("settlement.can_view_profitability");
  const canChangeConfirmedSettlement = useHasPermission(
    CAN_CHANGE_CONFIRMED_SETTLEMENT_PERMISSION
  );
  const canChangeSettlement = useHasPermission("settlement.change_settlement");
  const canAddInvoice = useHasPermission("invoice.add_invoice");
  const canViewInvoice = useHasPermission("invoice.view_invoice");
  const navigate = useNavigate();
  const { createInvoiceFromSettlement, loading: invoiceLoading } = useInvoices();
  const [invoicingSettlementId, setInvoicingSettlementId] = useState<number | null>(
    null
  );
  const [reopenSettlementId, setReopenSettlementId] = useState<number | null>(null);
  const [reopening, setReopening] = useState(false);
  const [sendReminderContractId, setSendReminderContractId] = useState<number | null>(null);

  const handleReopenSettlement = async () => {
    if (reopenSettlementId === null) {
      return;
    }

    setReopening(true);
    try {
      await axios.post(`${SETTLEMENTS_ENDPOINT}${reopenSettlementId}/reopen/`);
      const reopenedId = reopenSettlementId;
      setReopenSettlementId(null);
      window.dispatchEvent(new Event("settlementUpdated"));
      setActionModalSettlementId(reopenedId);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        t("Failed to reopen settlement");
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } finally {
      setReopening(false);
    }
  };

  const renderEquipmentLinks = (settlement: SettlementSummaryItem) => {
    if (settlement.equipments && settlement.equipments.length > 0) {
      return (
        <Box component="span" sx={{ display: "inline-flex", flexWrap: "wrap", gap: 0.5 }}>
          {settlement.equipments.map((equipment, index) => (
            <Box component="span" key={equipment.id ?? index}>
              <EntityReferenceLink
                entityType="equipment"
                entityId={equipment.id}
                label={equipment.name}
                canPreview={canViewEquipment}
                stopPropagation={false}
              />
              {index < settlement.equipments!.length - 1 ? ", " : ""}
            </Box>
          ))}
        </Box>
      );
    }
    return settlement.equipment_name;
  };

  const renderRentPrice = (settlement: SettlementSummaryItem) => (
    <>
      {formatNumber(settlement.rent_price)}
      {settlement.proportions_factor != null && settlement.proportions_factor !== "" ? (
        <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 0.25 }}>
          {t("(included proportions of: {{factor}})", {
            factor: formatNumber(settlement.proportions_factor),
          })}
        </Typography>
      ) : null}
    </>
  );

  const renderProfitabilityValue = (
    net: number | string | null | undefined,
    percent: number | string | null | undefined
  ) => {
    const netNumber = Number(net ?? 0);
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, color: netNumber < 0 ? "var(--errorColor, #d32f2f)" : undefined }}>
          {formatNumber(net)}
        </span>
        {percent != null && percent !== "" ? (
          <Chip
            size="small"
            variant="outlined"
            color={netNumber < 0 ? "error" : "success"}
            label={`${formatNumber(percent)}%`}
          />
        ) : null}
      </Box>
    );
  };

  const renderProfitabilityCost = (
    cost: number | string | null | undefined,
    jobCount: number | null | undefined
  ) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      <span>{formatNumber(cost)}</span>
      {jobCount ? (
        <Chip
          size="small"
          variant="outlined"
          icon={<Construction fontSize="small" />}
          label={jobCount}
          title={t("Jobs included in this cost")}
        />
      ) : null}
    </Box>
  );

  // Only ever rendered for a non-zero value (a zero row is hidden entirely), so
  // the amber chip always means "parts were consumed in this period" - matching
  // the Additional costs column on the Profitability tab.
  const renderAdditionalCosts = (value: number | string | null | undefined) => (
    <Chip
      size="small"
      color="warning"
      variant="filled"
      label={formatNumber(value)}
    />
  );

  const renderBruttoChip = () => (
    <Chip
      label={t("Gross (Brutto)")}
      color="warning"
      size="small"
      title={t("This contract's values are stored and settled as gross (brutto) amounts")}
    />
  );

  const renderTotalValue = (value: React.ReactNode, showBrutto: boolean) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      {value}
      {showBrutto ? renderBruttoChip() : null}
    </Box>
  );

  const renderSettlementTotal = (settlement: SettlementSummaryItem, showBrutto: boolean) => {
    if (
      settlement.total_overridden != null &&
      settlement.total_overridden !== settlement.total_calculated
    ) {
      return renderTotalValue(
        <>
          <Typography
            component="span"
            variant="body2"
            color="textSecondary"
            sx={{ textDecoration: "line-through", display: "block", width: "100%" }}
          >
            {formatNumber(settlement.total_calculated)}
          </Typography>
          <span className={styles.totalCell}>{formatNumber(settlement.total_overridden)}</span>
        </>,
        showBrutto
      );
    }

    return renderTotalValue(
      <span className={styles.totalCell}>{formatNumber(settlement.display_total)}</span>,
      showBrutto
    );
  };

  // Wraps a counter value in a hover tooltip showing when it was read,
  // instead of giving the read date its own line - keeps position table rows
  // compact while still surfacing it on demand.
  const withReadAtTooltip = (content: React.ReactNode, readAt?: string | null) => {
    if (!readAt) {
      return content;
    }

    return (
      <Tooltip title={t("Read on {{date}}", { date: formatDateTime(readAt) })} arrow>
        <Box
          component="span"
          sx={{ cursor: "help", borderBottom: "1px dashed", borderBottomColor: "divider" }}
        >
          {content}
        </Box>
      </Tooltip>
    );
  };

  const renderNewValueCell = (pos: SettlementPositionSummary, exceedsLimit: boolean) =>
    withReadAtTooltip(
      <>
        {formatCounterValue(pos.new_counter_value)}
        <br />
        <Typography
          variant="caption"
          component="span"
          className={styles.positionNumericValue}
          sx={{
            fontSize: exceedsLimit ? "1rem" : "0.9rem",
            fontWeight: exceedsLimit ? 700 : 400,
            color: exceedsLimit ? "text.primary" : "text.secondary",
          }}
        >
          <i>+{formatCounterValue(pos.counter_difference)}</i>
        </Typography>
      </>,
      pos.new_read_at
    );

  const renderPositionTable = (
    settlement: SettlementSummaryItem,
    showTitle: boolean = true
  ) => {
    if (!settlement.positions || settlement.positions.length === 0) {
      return null;
    }

    return (
      <TableContainer component={Paper} variant="outlined" className={styles.noScrollTable}>
        {showCounterReadings ? renderCounterReadingsBar(settlement) : null}
        <Table size="small" className={styles.squeezeTable}>
          <TableHead>
            {showTitle ? (
              <TableRow>
                <TableCell colSpan={5} className={styles.positionTableTitleCell}>
                  <Typography variant="subtitle2">{renderEquipmentLinks(settlement)}</Typography>
                  {settlement.equipment_serial ? (
                    <Typography variant="body2" className={styles.equipmentCodeBold}>
                      {settlement.equipment_serial}
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            ) : null}
            <TableRow className={styles.tableHeader}>
              <TableCell>{t("Counter")}</TableCell>
              <TableCell align="right">{t("Last value")}</TableCell>
              <TableCell align="right">{t("New value")}</TableCell>
              <TableCell align="right">{t("Price/Unit")}</TableCell>
              <TableCell align="right">{t("Included in rent")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {settlement.positions.map((pos, posIdx) => {
              const exceedsLimit = (pos.quantity_over_included_in_rent ?? 0) > 0;

              return (
                <TableRow key={`${settlement.settlement_id}-${pos.position_id}-${posIdx}`}>
                  <TableCell>{pos.counter_kind}</TableCell>
                  <TableCell align="right" className={styles.positionNumericValue}>
                    {pos.last_counter_value !== null
                      ? withReadAtTooltip(
                          formatCounterValue(pos.last_counter_value),
                          pos.last_read_at
                        )
                      : "-"}
                  </TableCell>
                  <TableCell align="right" className={styles.positionNumericValue}>
                    {renderNewValueCell(pos, exceedsLimit)}
                  </TableCell>
                  <TableCell align="right" className={styles.positionNumericValue}>
                    {formatNumber(pos.price_per_unit)}
                  </TableCell>
                  <TableCell align="right" className={styles.positionNumericValue}>
                    {formatCounterValue(pos.included_in_rent)}
                    <br />
                    <Typography
                      variant="caption"
                      component="span"
                      className={styles.positionNumericValue}
                      sx={{
                        fontSize: exceedsLimit ? "1rem" : "0.9rem",
                        fontWeight: exceedsLimit ? 700 : 400,
                        color: exceedsLimit ? "text.primary" : "text.secondary",
                      }}
                    >
                      <i>+{formatCounterValue(pos.quantity_over_included_in_rent ?? 0)}</i>
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // formatDateTime breaks the time onto its own line (it feeds two-line table
  // badges); the counter line is deliberately one line, so flatten it.
  const formatReadAt = (value?: string | null) => formatDateTime(value).replace("\n", " ");

  // Where one batch of readings came from and when it was taken, as one
  // inline entry: an icon for the source, its name, and the read date. The
  // values themselves are the table right underneath, so they are not
  // repeated here. `showKinds` names the counters the batch covers - only
  // needed when a settlement has more than one batch to tell apart.
  const renderCounterReadingSource = (group: CounterReadingGroup, showKinds: boolean) => {
    const SourceIcon = (group.source && COUNTER_SOURCE_ICONS[group.source]) || Speed;
    const sourceLabelKey = group.source ? COUNTER_SOURCE_LABEL_KEYS[group.source] : null;
    const sourceLabel = sourceLabelKey
      ? t(sourceLabelKey)
      : group.sourceDisplay || t("Unknown source");
    const kindLabels = group.readings
      .map((reading) =>
        reading.kindCode && COUNTER_KIND_SHORT_LABEL_KEYS[reading.kindCode]
          ? t(COUNTER_KIND_SHORT_LABEL_KEYS[reading.kindCode])
          : reading.kindLabel
      )
      .join(", ");

    return (
      <Tooltip
        key={group.key}
        arrow
        title={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ fontWeight: 700 }}>{sourceLabel}</Box>
            {group.readAt ? (
              <Box>{t("Read on {{date}}", { date: formatReadAt(group.readAt) })}</Box>
            ) : null}
            {group.readings.map((reading) => (
              <Box key={reading.key}>{reading.kindLabel}</Box>
            ))}
          </Box>
        }
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            maxWidth: "100%",
            cursor: "help",
          }}
        >
          <SourceIcon
            sx={{
              fontSize: 15,
              color: (group.source && COUNTER_SOURCE_COLORS[group.source]) || "text.secondary",
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {showKinds ? (
              <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                {`${kindLabels}:`}
              </Box>
            ) : null}
            {sourceLabel}
            {group.readAt ? ` · ${formatReadAt(group.readAt)}` : ""}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  // One caption line at the top of the position table, so the origin of the
  // readings sits with the values it explains rather than off in the card's
  // detail area. Hidden behind the "Counter readings" toolbar toggle.
  const renderCounterReadingsBar = (settlement: SettlementSummaryItem) => {
    const groups = buildCounterReadingGroups(settlement.positions || []);
    // Black and color pages are supplied together, so one batch is the norm;
    // more than one means the readings did not arrive at the same time and
    // each needs naming.
    const showKinds = groups.length > 1;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 1.5,
          rowGap: 0.25,
          px: 1,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      >
        {groups.length === 0 ? (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
            <Speed sx={{ fontSize: 15, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {t("No counter reading yet")}
            </Typography>
          </Box>
        ) : (
          groups.map((group) => renderCounterReadingSource(group, showKinds))
        )}
      </Box>
    );
  };

  const renderCounterUsageSummary = (settlement: SettlementSummaryItem) => {
    const positions = settlement.positions || [];
    if (positions.length === 0) {
      return null;
    }

    const differenceByKind = new Map<string, number>();
    positions.forEach((pos) => {
      const current = differenceByKind.get(pos.counter_kind) || 0;
      differenceByKind.set(pos.counter_kind, current + Number(pos.counter_difference || 0));
    });

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
        <Typography variant="subtitle2">{t("Counter usage summary")}</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {Array.from(differenceByKind.entries()).map(([kind, total]) => (
            <Typography key={kind} variant="body2">
              {kind}: <strong>+{formatCounterValue(total)}</strong>
            </Typography>
          ))}
        </Box>
      </Box>
    );
  };

  const renderDetailItem = (label: string, value: React.ReactNode) => (
    <Box className={styles.detailItem}>
      <Typography
        variant="caption"
        color="textSecondary"
        component="div"
        className={styles.detailLabel}
      >
        {label}
      </Typography>
      <Box className={styles.detailValue}>{value}</Box>
    </Box>
  );

  /**
   * Raise an invoice for one settlement straight from the sale summary and go
   * to it. Only offered for confirmed settlements that have none yet - the
   * backend refuses anything else.
   */
  const handleGenerateInvoice = async (settlementId: number) => {
    setInvoicingSettlementId(settlementId);
    const result = await createInvoiceFromSettlement(settlementId);
    setInvoicingSettlementId(null);

    if (result?.success && result.data?.id) {
      enqueueSnackbar(t("Invoice has been created"), SUCCESS_SNACKBAR);
      navigate(`/invoices/${result.data.id}`);
      return;
    }

    enqueueSnackbar(
      result?.errorData?.message || t("Could not create the invoice"),
      ERROR_SNACKBAR
    );
  };

  const renderSettlementCard = (settlement: SettlementSummaryItem, showBrutto: boolean) => {
    const statusColor = SETTLEMENT_STATUS_TO_COLOR[settlement.status];
    const showExtraPositions = Number(settlement.extra_positions_total) !== 0;
    const showInflation = hasInflationRate(settlement.inflation_rate_percent);

    return (
      <Box
        key={settlement.settlement_id}
        className={styles.settlementCard}
        sx={{
          borderLeft: "4px solid",
          borderLeftColor: statusColor ? `${statusColor}.main` : "divider",
        }}
      >
        <Box className={styles.settlementHeader}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              <Typography variant="subtitle2">{renderEquipmentLinks(settlement)}</Typography>
              {settlement.is_first_settlement ? (
                <Tooltip
                  title={t(
                    "This equipment was newly attached to this contract - first settlement for it here"
                  )}
                >
                  <Box sx={statusIconBadgeSx("success")}>
                    <AddLink fontSize="small" />
                  </Box>
                </Tooltip>
              ) : null}
              {settlement.term_ended ? (
                <Tooltip title={t("This equipment's term has already been ended on this contract")}>
                  <Box sx={statusIconBadgeSx("warning")}>
                    <LinkOff fontSize="small" />
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
            {settlement.equipment_serial ? (
              <Typography variant="body2" className={styles.equipmentCodeBold}>
                {settlement.equipment_serial}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {settlement.sale_date ? (
              <Chip label={settlement.sale_date} variant="outlined" size="small" />
            ) : null}
            <Chip label={settlement.status_display} color={statusColor || "default"} size="small" />
            {settlement.invoice ? (
              <Tooltip
                title={`${t("Invoice")}: ${
                  settlement.invoice.number || t("Draft")
                }`}
              >
                <Chip
                  size="small"
                  icon={<ReceiptLong fontSize="small" />}
                  label={settlement.invoice.number || t("Draft")}
                  color={settlement.invoice.is_paid ? "success" : "default"}
                  variant="outlined"
                  onClick={
                    canViewInvoice
                      ? () => navigate(`/invoices/${settlement.invoice!.id}`)
                      : undefined
                  }
                />
              </Tooltip>
            ) : settlement.status === SETTLEMENT_STATUS_CONFIRMED && canAddInvoice ? (
              <Tooltip title={t("Generate invoice")}>
                <span>
                  <IconButton
                    size="small"
                    disabled={
                      invoiceLoading &&
                      invoicingSettlementId === settlement.settlement_id
                    }
                    onClick={() => handleGenerateInvoice(settlement.settlement_id)}
                  >
                    <ReceiptLong fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {settlement.status !== SETTLEMENT_STATUS_CONFIRMED || canChangeConfirmedSettlement ? (
              <Tooltip title={t("Manage settlement")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (settlement.status === SETTLEMENT_STATUS_CONFIRMED) {
                      setReopenSettlementId(settlement.settlement_id);
                    } else {
                      setActionModalSettlementId(settlement.settlement_id);
                    }
                  }}
                >
                  <EditNote fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>

        <Box className={styles.detailGrid}>
          {renderDetailItem(t("Rent price"), renderRentPrice(settlement))}
          {renderDetailItem(t("Total for Usage"), formatNumber(settlement.calculated_sum))}
          {showExtraPositions
            ? renderDetailItem(
                t("Extra positions"),
                formatNumber(settlement.extra_positions_total)
              )
            : null}
          {showInflation
            ? renderDetailItem(
                t("Inflation"),
                formatInflationRatePercent(settlement.inflation_rate_percent)
              )
            : null}
          {renderDetailItem(t("Total"), renderSettlementTotal(settlement, showBrutto))}
          {canViewProfitability && settlement.profitability_cost != null
            ? renderDetailItem(
                t("Cost"),
                renderProfitabilityCost(
                  settlement.profitability_cost,
                  settlement.profitability_job_count
                )
              )
            : null}
          {canViewProfitability && settlement.profitability_net != null
            ? renderDetailItem(
                t("Profitability (net)"),
                renderProfitabilityValue(
                  settlement.profitability_net,
                  settlement.profitability_percent
                )
              )
            : null}
          {canViewProfitability &&
          settlement.additional_costs != null &&
          Number(settlement.additional_costs) !== 0
            ? renderDetailItem(
                t("Additional costs"),
                renderAdditionalCosts(settlement.additional_costs)
              )
            : null}
        </Box>

        {settlement.positions && settlement.positions.length > 0 ? (
          <Box className={styles.positionsSection}>
            {renderPositionTable(settlement, false)}
            {settlement.is_common_counter ? renderCounterUsageSummary(settlement) : null}
          </Box>
        ) : null}
      </Box>
    );
  };

  const renderUsageByKindSummary = (usageByKind: Record<string, number>) => {
    const entries = Object.entries(usageByKind || {});
    if (entries.length === 0) {
      return null;
    }

    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {entries.map(([kind, total]) => (
          <Typography key={kind} variant="body2">
            {kind}: <strong>+{formatCounterValue(total)}</strong>
          </Typography>
        ))}
      </Box>
    );
  };

  const renderContractSettlements = (
    contractSummary: SaleDateContractSummary,
    client?: ClientOnSaleSummary
  ) => {
    const showExtraPositionsTotal = Number(contractSummary.totals.extra_positions_total) !== 0;
    const hasMultipleSettlements = contractSummary.settlements.length > 1;
    const contractNeedsReminder = contractSummary.settlements.some(
      (settlement) => settlement.status === SETTLEMENT_STATUS_NEEDS_INVESTIGATION
    );

    return (
      <>
        <Box className={styles.contractHeader}>
          <Handshake fontSize="small" />
          <Typography
            component="span"
            variant="body2"
            sx={{ display: "flex", alignItems: "center", fontWeight: 700 }}
          >
            <EntityReferenceLink
              entityType="contract"
              entityId={contractSummary.contract_id}
              label={[contractSummary.contract_number, contractSummary.contract_internal_name]
                .filter(Boolean)
                .join(" - ")}
              canPreview={canViewContracts}
              stopPropagation={false}
            />
          </Typography>
          {contractSummary.contract_ended ? (
            <Tooltip title={t("This contract has already ended")}>
              <Box sx={statusIconBadgeSx("warning")}>
                <EventBusy fontSize="small" />
              </Box>
            </Tooltip>
          ) : null}
          {client ? (
            <Typography
              component="span"
              variant="body2"
              color="textSecondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <Person4 fontSize="small" />
              <EntityReferenceLink
                entityType="client"
                entityId={client.id}
                label={client.short_name || client.name}
                canPreview={canViewClients}
                stopPropagation={false}
              />
              {client.nip ? (
                <Box component="span" sx={{ ml: 0.5 }}>
                  {t("NIP")}: {client.nip}
                </Box>
              ) : null}
            </Typography>
          ) : null}
          {canChangeSettlement && contractNeedsReminder ? (
            <Tooltip title={t("Send reminder")}>
              <IconButton
                size="small"
                onClick={() => setSendReminderContractId(contractSummary.contract_id)}
                sx={{ ml: "auto" }}
              >
                <Email fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1.5 }}>
          {contractSummary.settlements.map((settlement) =>
            renderSettlementCard(settlement, !hasMultipleSettlements && !!contractSummary.is_brutto)
          )}
        </Box>

        {hasMultipleSettlements ? (
          <Box className={styles.summaryRow}>
            <Typography variant="subtitle2">{t("Summary")}</Typography>
            {renderUsageByKindSummary(contractSummary.totals.usage_by_kind)}
            <Box className={styles.detailGrid}>
              {renderDetailItem(
                t("Total for Usage"),
                formatNumber(contractSummary.totals.usage_calculated)
              )}
              {showExtraPositionsTotal
                ? renderDetailItem(
                    t("Extra positions"),
                    formatNumber(contractSummary.totals.extra_positions_total)
                  )
                : null}
              {renderDetailItem(
                t("Total"),
                renderTotalValue(
                  <span className={styles.totalCell}>
                    {formatNumber(contractSummary.totals.display_total)}
                  </span>,
                  !!contractSummary.is_brutto
                )
              )}
              {canViewProfitability && contractSummary.totals.has_profitability
                ? renderDetailItem(
                    t("Cost"),
                    renderProfitabilityCost(
                      contractSummary.totals.profitability_cost,
                      contractSummary.totals.profitability_job_count
                    )
                  )
                : null}
              {canViewProfitability && contractSummary.totals.has_profitability
                ? renderDetailItem(
                    t("Profitability (net)"),
                    renderProfitabilityValue(
                      contractSummary.totals.profitability_net,
                      Number(contractSummary.totals.display_total)
                        ? (Number(contractSummary.totals.profitability_net ?? 0) /
                            Number(contractSummary.totals.display_total)) *
                            100
                        : null
                    )
                  )
                : null}
              {canViewProfitability &&
              contractSummary.totals.has_profitability &&
              Number(contractSummary.totals.additional_costs) !== 0
                ? renderDetailItem(
                    t("Additional costs"),
                    renderAdditionalCosts(contractSummary.totals.additional_costs)
                  )
                : null}
            </Box>
            {contractSummary.totals.pending_count > 0 ? (
              <Typography variant="caption" color="textSecondary">
                {t("{{count}} pending settlement(s) not included in this total", {
                  count: contractSummary.totals.pending_count,
                })}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </>
    );
  };

  const canViewSaleSummary = useHasPermission("settlement.view_sale_summary");
  const [refreshKey, setRefreshKey] = useState(0);
  const [contractNumberSort, setContractNumberSort] = useState<"asc" | "desc">(
    "desc"
  );
  const [settlementFilter, setSettlementFilter] = useState<
    "all" | "fully_settled" | "not_settled"
  >("all");
  const [groupBy, setGroupBy] = useState<"client" | "none">("none");
  // Off by default: the counter strip is an answer to "where did this reading
  // come from?", not something every month needs on screen.
  const [showCounterReadings, setShowCounterReadings] = useState(false);
  const counterReadingsExtraHeight = showCounterReadings ? COUNTER_READINGS_LINE_HEIGHT : 0;
  const [actionModalSettlementId, setActionModalSettlementId] = useState<number | null>(
    null
  );

  // One entry per group for the whole filter set (from page 1's `manifest`),
  // null until page 1 lands. Its length is the row count and its entries drive
  // the skeleton rows' labels and height estimates.
  const [manifest, setManifest] = useState<SaleSummaryManifestRow[] | null>(null);
  // `count` from the paginated response - a fallback row count for the rare
  // case the manifest is missing.
  const [totalCount, setTotalCount] = useState<number | null>(null);
  // Loaded pages, keyed by 1-based page number -> that page's groups.
  const [pages, setPages] = useState<Map<number, SaleSummaryResponse[]>>(
    () => new Map()
  );
  // Per-page fetch errors for pages past the first (a page-1 failure is fatal
  // and handled via `error` instead).
  const [pageErrors, setPageErrors] = useState<Map<number, string>>(
    () => new Map()
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pages with a request already in flight - stops the same page being
  // re-requested while the user keeps scrolling across it.
  const inFlightPagesRef = useRef<Set<number>>(new Set());
  // Bumped on every filter change / refresh; a response tagged with an older
  // value is dropped so a slow request can't land into a fresh list.
  const requestVersionRef = useRef(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  // The scrollable ancestor the list lives in (the app "workspace" panel), so
  // the virtualizer tracks that scrollbar - the one the user drags - instead of
  // the window. Falls back to window scroll when nothing scrollable is found.
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);

  useEffect(() => {
    let element = containerRef.current?.parentElement ?? null;
    while (element) {
      const overflowY = window.getComputedStyle(element).overflowY;
      if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
        setScrollParent(element);
        return;
      }
      element = element.parentElement;
    }
    setScrollParent(null);
  }, []);

  // Number of virtualized rows: the manifest is authoritative once loaded; the
  // paginated `count` is the fallback before it (or if the backend omits it).
  const rowCount = manifest ? manifest.length : totalCount ?? 0;

  // A blended default for rows outside the rendered window - the average of the
  // manifest's per-row estimates, so the scrollbar length is about right from
  // the first frame instead of drifting as pages stream in.
  const defaultRowHeight = useMemo(() => {
    if (!manifest || manifest.length === 0) {
      return SALE_SUMMARY_ESTIMATED_ROW_HEIGHT;
    }
    const total = manifest.reduce(
      (sum, row) => sum + estimateRowHeight(row, counterReadingsExtraHeight),
      0
    );
    return Math.round(total / manifest.length);
  }, [manifest, counterReadingsExtraHeight]);

  const buildQueryParams = useCallback(
    (targetPage: number) => {
      const queryParams = new URLSearchParams({ sale_date: saleDate ?? "" });
      if (contractId) {
        queryParams.set("contract_id", contractId);
      }
      if (search) {
        queryParams.set("search", search);
      }
      queryParams.set("page", String(targetPage));
      queryParams.set("page_size", String(SALE_SUMMARY_PAGE_SIZE));
      queryParams.set("contract_number_sort", contractNumberSort);
      queryParams.set("group_by", groupBy);
      if (!contractId && settlementFilter === "fully_settled") {
        queryParams.set("fully_settled_only", "true");
      }
      if (!contractId && settlementFilter === "not_settled") {
        queryParams.set("not_settled_only", "true");
      }
      return queryParams;
    },
    [saleDate, contractId, search, contractNumberSort, settlementFilter, groupBy]
  );

  const fetchPage = useCallback(
    async (targetPage: number) => {
      if (!saleDate || inFlightPagesRef.current.has(targetPage)) {
        return;
      }
      const version = requestVersionRef.current;
      const isFirstPage = targetPage === 1;
      inFlightPagesRef.current.add(targetPage);
      setPageErrors((prev) => {
        if (!prev.has(targetPage)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(targetPage);
        return next;
      });
      if (isFirstPage) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await axios.get(
          `${SETTLEMENTS_ENDPOINT}sale-summary/?${buildQueryParams(targetPage).toString()}`
        );
        if (version !== requestVersionRef.current) {
          return;
        }
        const payload = response.data as
          | PaginatedResponse<SaleSummaryResponse>
          | SaleSummaryResponse[];
        const results = Array.isArray(payload) ? payload : payload?.results ?? [];
        const count = Array.isArray(payload)
          ? results.length
          : payload?.count ?? results.length;
        setTotalCount(count);
        if (isFirstPage && !Array.isArray(payload) && payload?.manifest) {
          setManifest(payload.manifest);
        }
        setPages((prev) => {
          const next = new Map(prev);
          next.set(targetPage, results);
          return next;
        });
      } catch (err: any) {
        if (version !== requestVersionRef.current) {
          return;
        }
        const message =
          err?.response?.data?.error ||
          err?.message ||
          (isFirstPage
            ? t("Failed to load summary")
            : t("Failed to load more results"));
        if (isFirstPage) {
          setError(message);
        } else {
          setPageErrors((prev) => {
            const next = new Map(prev);
            next.set(targetPage, message);
            return next;
          });
        }
        console.error("Error fetching sale summary page", targetPage, err);
      } finally {
        inFlightPagesRef.current.delete(targetPage);
        if (isFirstPage && version === requestVersionRef.current) {
          setLoading(false);
        }
      }
    },
    [saleDate, buildQueryParams, t]
  );

  // Ensure every page overlapping [startIndex, endIndex] is loaded or loading.
  // Virtuoso calls this whenever the rendered range changes - including right
  // after the scrollbar is dragged into the middle of the list - so only the
  // page(s) under the viewport get fetched, not everything before them.
  const ensurePagesForRange = useCallback(
    (startIndex: number, endIndex: number) => {
      if (rowCount === 0) {
        return;
      }
      const firstPage =
        Math.floor(Math.max(0, startIndex) / SALE_SUMMARY_PAGE_SIZE) + 1;
      const lastPage =
        Math.floor(Math.min(rowCount - 1, endIndex) / SALE_SUMMARY_PAGE_SIZE) + 1;
      for (let pageNumber = firstPage; pageNumber <= lastPage; pageNumber += 1) {
        if (
          !pages.has(pageNumber) &&
          !pageErrors.has(pageNumber) &&
          !inFlightPagesRef.current.has(pageNumber)
        ) {
          fetchPage(pageNumber);
        }
      }
    },
    [rowCount, pages, pageErrors, fetchPage]
  );

  const getGroupAt = useCallback(
    (index: number): SaleSummaryResponse | undefined => {
      const pageNumber = Math.floor(index / SALE_SUMMARY_PAGE_SIZE) + 1;
      return pages.get(pageNumber)?.[index % SALE_SUMMARY_PAGE_SIZE];
    },
    [pages]
  );

  useEffect(() => {
    const handleSettlementRefresh = () => {
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener("settlementConfirmed", handleSettlementRefresh);
    window.addEventListener("settlementUpdated", handleSettlementRefresh);
    window.addEventListener("equipmentCounterAdded", handleSettlementRefresh);
    return () => {
      window.removeEventListener("settlementConfirmed", handleSettlementRefresh);
      window.removeEventListener("settlementUpdated", handleSettlementRefresh);
      window.removeEventListener("equipmentCounterAdded", handleSettlementRefresh);
    };
  }, []);

  // Any change to what the list represents (date, contract, search, sort,
  // filter, grouping, or an external refresh) discards the loaded pages and
  // reloads from page 1.
  useEffect(() => {
    requestVersionRef.current += 1;
    inFlightPagesRef.current = new Set();
    setPages(new Map());
    setPageErrors(new Map());
    setManifest(null);
    setTotalCount(null);
    setError(null);
    // Back to the top - the previous filter's scroll offset is meaningless for
    // the new list.
    if (scrollParent) {
      scrollParent.scrollTop = 0;
    }

    if (!canViewSaleSummary || !saleDate) {
      setLoading(false);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canViewSaleSummary,
    saleDate,
    contractId,
    search,
    refreshKey,
    contractNumberSort,
    settlementFilter,
    groupBy,
  ]);

  if (!canViewSaleSummary) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t("You do not have permission to view the sale summary.")}
      </Alert>
    );
  }

  const sortToolbar = saleDate ? (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        width: "100%",
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={contractNumberSort}
        onChange={(_event, value: "asc" | "desc" | null) => {
          if (value !== null) {
            setContractNumberSort(value);
          }
        }}
        aria-label={t("Ordering") + ":"}
      >
        <ToggleButton value="desc">{t("Newest number first")}</ToggleButton>
        <ToggleButton value="asc">{t("Oldest number first")}</ToggleButton>
      </ToggleButtonGroup>

      {!contractId ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={settlementFilter}
          onChange={(
            _event,
            value: "all" | "fully_settled" | "not_settled" | null
          ) => {
            if (value !== null) {
              setSettlementFilter(value);
            }
          }}
          aria-label={t("Contracts") + ":"}
        >
          <ToggleButton value="all">{t("All contracts")}</ToggleButton>
          <ToggleButton value="fully_settled">{t("Fully settled only")}</ToggleButton>
          <ToggleButton value="not_settled">{t("Not settled only")}</ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      {!contractId ? (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={groupBy}
          onChange={(_event, value: "client" | "none" | null) => {
            if (value !== null) {
              setGroupBy(value);
            }
          }}
          aria-label={t("Grouping") + ":"}
        >
          <ToggleButton value="none">{t("Ungrouped")}</ToggleButton>
          <ToggleButton value="client">{t("Group by client")}</ToggleButton>
        </ToggleButtonGroup>
      ) : null}

      <Tooltip
        title={t(
          "Show where each settlement's counter readings came from and when they were taken"
        )}
      >
        <ToggleButton
          size="small"
          value="counter-readings"
          selected={showCounterReadings}
          onChange={() => setShowCounterReadings((previous) => !previous)}
          sx={{ gap: 0.5 }}
        >
          <Speed fontSize="small" />
          {t("Counter readings")}
        </ToggleButton>
      </Tooltip>
    </Box>
  ) : null;

  if (loading && pages.size === 0 && !error) {
    return (
      <Box
        ref={containerRef}
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        {sortToolbar}
        <CustomCircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        ref={containerRef}
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        {sortToolbar}
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box sx={{ color: "error.main" }}>{error}</Box>
          <Button variant="outlined" size="small" onClick={() => fetchPage(1)}>
            {t("Retry")}
          </Button>
        </Box>
      </Box>
    );
  }

  if (rowCount === 0) {
    return (
      <Box
        ref={containerRef}
        sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}
      >
        {sortToolbar}
        <Box sx={{ p: 3 }}>{t("No data available")}</Box>
      </Box>
    );
  }

  const renderGroup = (group: SaleSummaryResponse) => {
    if (groupBy === "none") {
      return (
        <Box sx={{ mb: 2 }}>
          {group.contracts.map((contractSummary, contractIdx) => (
            <Box
              key={contractSummary.contract_id ?? contractIdx}
              sx={{ mb: contractIdx < group.contracts.length - 1 ? 2 : 0 }}
            >
              {renderContractSettlements(contractSummary, group.client)}
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Box className={styles.section} sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Person4 />
          <EntityReferenceLink
            entityType="client"
            entityId={group.client.id}
            label={group.client.short_name || group.client.name}
            canPreview={canViewClients}
            stopPropagation={false}
          />
          {group.client.nip ? (
            <Typography component="span" variant="body2" color="textSecondary">
              {t("NIP")}: {group.client.nip}
            </Typography>
          ) : null}
        </Typography>

        {group.contracts.map((contractSummary, contractIdx) => (
          <Box
            sx={{ mb: 2 }}
            key={`${group.client.id}-${contractSummary.contract_number}-${contractIdx}`}
          >
            {renderContractSettlements(contractSummary)}
          </Box>
        ))}
      </Box>
    );
  };

  // Not-yet-loaded row: show the group's identity (from the manifest) straight
  // away plus a body skeleton sized from its settlement count, so scrolling past
  // it reads as a real list entry rather than a blank shimmer, and its height is
  // close enough that swapping in the real card barely moves the scrollbar.
  const renderPlaceholder = (index: number) => {
    const row = manifest?.[index];
    const pageNumber = Math.floor(index / SALE_SUMMARY_PAGE_SIZE) + 1;
    const pageError = pageErrors.get(pageNumber);

    const heading =
      row?.type === "contract"
        ? [row.contract_number, row.contract_internal_name]
            .filter(Boolean)
            .join(" - ")
        : row?.type === "client"
          ? row.client_short_name || row.client_name
          : "";

    return (
      <Box sx={{ mb: 2 }}>
        {row ? (
          <Box className={styles.contractHeader} sx={{ mb: 1 }}>
            {row.type === "client" ? (
              <Person4 fontSize="small" />
            ) : (
              <Handshake fontSize="small" />
            )}
            <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
              {heading}
            </Typography>
            {row.type === "contract" ? (
              <Typography
                component="span"
                variant="body2"
                color="textSecondary"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Person4 fontSize="small" />
                {row.client_short_name || row.client_name}
              </Typography>
            ) : null}
          </Box>
        ) : null}

        {pageError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => fetchPage(pageNumber)}
              >
                {t("Retry")}
              </Button>
            }
          >
            {pageError}
          </Alert>
        ) : (
          <Skeleton
            variant="rounded"
            height={Math.max(
              80,
              estimateRowHeight(row, counterReadingsExtraHeight) - (row ? 52 : 0)
            )}
            sx={{ borderRadius: 2 }}
          />
        )}
      </Box>
    );
  };

  const renderVirtualRow = (index: number) => {
    const group = getGroupAt(index);
    if (group) {
      return renderGroup(group);
    }
    return renderPlaceholder(index);
  };

  return (
    <Box className={styles.container} ref={containerRef}>
      {sortToolbar ? (
        <Box sx={{ mb: 2, width: "100%" }}>{sortToolbar}</Box>
      ) : null}
      {showHeader && (
        <Typography variant="h6" className={styles.header}>
          {t("Sale summary")} ({saleDate})
        </Typography>
      )}

      <Virtuoso
        customScrollParent={scrollParent ?? undefined}
        useWindowScroll={!scrollParent}
        totalCount={rowCount}
        defaultItemHeight={defaultRowHeight}
        increaseViewportBy={{ top: 600, bottom: 1600 }}
        computeItemKey={(index) => {
          // The manifest row's id is stable across the placeholder -> loaded
          // swap, so the card fills in without a remount.
          const row = manifest?.[index];
          if (row) {
            return `${row.type}-${row.id}`;
          }
          const group = getGroupAt(index);
          if (group) {
            return groupBy === "none"
              ? `contract-${group.contracts[0]?.contract_id ?? index}`
              : `client-${group.client.id}`;
          }
          return `pending-${index}`;
        }}
        rangeChanged={({ startIndex, endIndex }) =>
          ensurePagesForRange(startIndex, endIndex)
        }
        itemContent={(index) => renderVirtualRow(index)}
      />

      <SettlementActionModal
        settlementId={actionModalSettlementId}
        opened={actionModalSettlementId !== null}
        setOpened={(value) => {
          if (!value) {
            setActionModalSettlementId(null);
          }
        }}
      />

      <CustomModal
        opened={sendReminderContractId !== null}
        setOpened={(value) => {
          if (!value) {
            setSendReminderContractId(null);
          }
        }}
        title={t("Send reminder")}
        content={
          <SendReminder
            preselectedContractId={
              sendReminderContractId !== null ? String(sendReminderContractId) : undefined
            }
            onSuccess={() => {
              setSendReminderContractId(null);
              window.dispatchEvent(new Event("settlementUpdated"));
            }}
          />
        }
      />

      <CustomModal
        opened={reopenSettlementId !== null}
        setOpened={(value) => {
          if (!value) {
            setReopenSettlementId(null);
          }
        }}
        title={t("Edit confirmed settlement")}
        width={480}
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body1">
              {t(
                "Are you sure you want to edit this confirmed settlement? It will be moved back to an editable state and its totals recalculated."
              )}
            </Typography>
            <Alert severity="warning">
              <Typography variant="body2">
                {t("This settlement is already confirmed.")}
              </Typography>
            </Alert>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={() => setReopenSettlementId(null)}
                disabled={reopening}
              >
                {t("Cancel")}
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={handleReopenSettlement}
                disabled={reopening}
              >
                {reopening ? t("Processing...") : t("Confirm")}
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};
